import { useState, useCallback } from 'react';
import {
  processImage,
  processCroppedImage,
  uploadProductImage,
  updateProductImageUrls,
  deleteProductImage,
  isValidImageFormat,
  IMAGE_ERROR_MESSAGES,
  type ProcessedImages,
  type ImageUploadErrorType,
} from '@paulownia/shared';

export type ImageUploadStatus =
  | 'idle'
  | 'selecting'
  | 'cropping'
  | 'processing'
  | 'uploading'
  | 'success'
  | 'error';

export interface ImageUploadState {
  status: ImageUploadStatus;
  progress: number;
  error: string | null;
  previewUrl: string | null;
  pendingBlob: Blob | null;
  processedImages: ProcessedImages | null;
}

const initialState: ImageUploadState = {
  status: 'idle',
  progress: 0,
  error: null,
  previewUrl: null,
  pendingBlob: null,
  processedImages: null,
};

export function useImageUpload(tenantId: string) {
  const [state, setState] = useState<ImageUploadState>(initialState);

  const reset = useCallback(() => {
    // Clean up preview URL if exists
    if (state.previewUrl) {
      URL.revokeObjectURL(state.previewUrl);
    }
    setState(initialState);
  }, [state.previewUrl]);

  const setError = useCallback((errorType: ImageUploadErrorType) => {
    setState(prev => ({
      ...prev,
      status: 'error',
      error: IMAGE_ERROR_MESSAGES[errorType],
    }));
  }, []);

  /**
   * Handle file selection (from camera or gallery)
   * Returns the preview URL for cropping
   */
  const selectFile = useCallback(async (file: File): Promise<string | null> => {
    // Validate format
    if (!isValidImageFormat(file)) {
      setError('INVALID_FORMAT');
      return null;
    }

    // Clean up previous preview URL
    if (state.previewUrl) {
      URL.revokeObjectURL(state.previewUrl);
    }

    // Create preview URL for cropping
    const previewUrl = URL.createObjectURL(file);

    setState({
      status: 'cropping',
      progress: 0,
      error: null,
      previewUrl,
      pendingBlob: file,
      processedImages: null,
    });

    return previewUrl;
  }, [state.previewUrl, setError]);

  /**
   * Process the cropped image blob
   * Returns the processed images directly to avoid async state issues
   */
  const processCrop = useCallback(async (croppedBlob: Blob): Promise<ProcessedImages | null> => {
    setState(prev => ({
      ...prev,
      status: 'processing',
      pendingBlob: croppedBlob,
    }));

    try {
      const processedImages = await processCroppedImage(croppedBlob);

      setState(prev => ({
        ...prev,
        status: 'idle', // Ready to upload
        processedImages,
      }));

      return processedImages;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'PROCESSING_FAILED';
      setError(errorMessage as ImageUploadErrorType);
      return null;
    }
  }, [setError]);

  /**
   * Process a file directly (skip cropping)
   */
  const processFile = useCallback(async (file: File): Promise<boolean> => {
    if (!isValidImageFormat(file)) {
      setError('INVALID_FORMAT');
      return false;
    }

    setState(prev => ({
      ...prev,
      status: 'processing',
      pendingBlob: file,
    }));

    try {
      const processedImages = await processImage(file);

      setState(prev => ({
        ...prev,
        status: 'idle', // Ready to upload
        processedImages,
      }));

      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'PROCESSING_FAILED';
      setError(errorMessage as ImageUploadErrorType);
      return false;
    }
  }, [setError]);

  /**
   * Upload the processed images to Firebase Storage
   * @param productId - The product ID
   * @param images - Optional processed images (if not provided, uses state.processedImages)
   */
  const upload = useCallback(async (productId: string, images?: ProcessedImages): Promise<{ imageUrl: string; thumbnailUrl: string } | null> => {
    const imagesToUpload = images || state.processedImages;
    if (!imagesToUpload) {
      console.error('No processed images to upload');
      return null;
    }

    setState(prev => ({
      ...prev,
      status: 'uploading',
      progress: 0,
    }));

    try {
      const result = await uploadProductImage(
        tenantId,
        productId,
        imagesToUpload,
        (progress) => {
          setState(prev => ({
            ...prev,
            progress,
          }));
        }
      );

      // Update product with new URLs
      await updateProductImageUrls(productId, result.imageUrl, result.thumbnailUrl);

      setState(prev => ({
        ...prev,
        status: 'success',
        progress: 100,
      }));

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'UPLOAD_FAILED';
      setError(errorMessage as ImageUploadErrorType);
      return null;
    }
  }, [tenantId, state.processedImages, setError]);

  /**
   * Retry the upload with the pending blob
   */
  const retry = useCallback(async (productId: string): Promise<{ imageUrl: string; thumbnailUrl: string } | null> => {
    if (!state.pendingBlob) {
      console.error('No pending blob to retry');
      return null;
    }

    // Re-process and upload
    setState(prev => ({
      ...prev,
      status: 'processing',
      error: null,
    }));

    try {
      const processedImages = await processCroppedImage(state.pendingBlob);

      setState(prev => ({
        ...prev,
        processedImages,
        status: 'uploading',
        progress: 0,
      }));

      const result = await uploadProductImage(
        tenantId,
        productId,
        processedImages,
        (progress) => {
          setState(prev => ({
            ...prev,
            progress,
          }));
        }
      );

      await updateProductImageUrls(productId, result.imageUrl, result.thumbnailUrl);

      setState(prev => ({
        ...prev,
        status: 'success',
        progress: 100,
      }));

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'UPLOAD_FAILED';
      setError(errorMessage as ImageUploadErrorType);
      return null;
    }
  }, [tenantId, state.pendingBlob, setError]);

  /**
   * Delete the product image
   */
  const deleteImage = useCallback(async (productId: string): Promise<boolean> => {
    try {
      await deleteProductImage(tenantId, productId);
      reset();
      return true;
    } catch (error) {
      console.error('Error deleting image:', error);
      return false;
    }
  }, [tenantId, reset]);

  /**
   * Cancel cropping and return to idle
   */
  const cancelCrop = useCallback(() => {
    if (state.previewUrl) {
      URL.revokeObjectURL(state.previewUrl);
    }
    setState(initialState);
  }, [state.previewUrl]);

  return {
    state,
    selectFile,
    processCrop,
    processFile,
    upload,
    retry,
    deleteImage,
    cancelCrop,
    reset,
    isReady: state.processedImages !== null && state.status === 'idle',
    isUploading: state.status === 'uploading',
    isProcessing: state.status === 'processing',
    isCropping: state.status === 'cropping',
    hasError: state.status === 'error',
  };
}
